import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import EducationModuleManager from '../../components/EducationModuleManager';
import AdminCreditReport from '../../components/AdminCreditReport';

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [stats, setStats] = useState(null);
  const [rateLimitSettings, setRateLimitSettings] = useState({ enabled: true, maxAttempts: 5, windowMs: 900000 });
  const [demoModeMinutes, setDemoModeMinutes] = useState(60);
  const [updatingRateLimit, setUpdatingRateLimit] = useState(false);
  const [educationModules, setEducationModules] = useState([]);
  const [loadingEducation, setLoadingEducation] = useState(false);
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    fetchUsers();
    fetchStats();
    fetchRateLimitSettings();
    fetchEducationModules();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch('/api/admin/stats', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/users', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRateLimitSettings = async () => {
    try {
      const res = await fetch('/api/admin/settings/rate-limit', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setRateLimitSettings(data);
    } catch (error) {
      console.error('Failed to fetch rate limit settings:', error);
    }
  };

  const fetchEducationModules = async () => {
    setLoadingEducation(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/education-modules', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEducationModules(data);
      }
    } catch (err) {
      console.error('Failed to fetch education modules:', err);
    } finally {
      setLoadingEducation(false);
    }
  };

  const updateRateLimit = async () => {
    setUpdatingRateLimit(true);
    try {
      const res = await fetch('/api/admin/settings/rate-limit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(rateLimitSettings)
      });
      if (res.ok) {
        alert('Rate limit settings updated');
        fetchRateLimitSettings();
      } else {
        alert('Failed to update');
      }
    } catch (error) {
      alert('Error updating settings');
    } finally {
      setUpdatingRateLimit(false);
    }
  };

  const enableDemoMode = async () => {
    setUpdatingRateLimit(true);
    try {
      const res = await fetch('/api/admin/settings/rate-limit/demo-mode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ durationMinutes: demoModeMinutes })
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Demo mode enabled until ${new Date(data.expiresAt).toLocaleTimeString()}`);
        fetchRateLimitSettings();
      } else {
        alert('Failed to enable demo mode');
      }
    } catch (error) {
      alert('Error enabling demo mode');
    } finally {
      setUpdatingRateLimit(false);
    }
  };

  const handleAdjustCredits = async (userId) => {
    if (!creditAmount || !creditReason) {
      alert('Please enter amount and reason');
      return;
    }
    const amount = parseFloat(creditAmount);
    if (isNaN(amount)) {
      alert('Please enter a valid number');
      return;
    }
    
    setAdjusting(true);
    try {
      const res = await fetch(`/api/admin/users/${userId}/credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          amount,
          reason: creditReason,
          issuerId: null,
          issuerType: 'platform'
        })
      });
      if (res.ok) {
        alert(`Credits ${amount > 0 ? 'added' : 'removed'} successfully`);
        setCreditAmount('');
        setCreditReason('');
        setSelectedUser(null);
        fetchUsers();
      } else {
        const error = await res.json();
        alert(error.error || 'Failed to adjust credits');
      }
    } catch (error) {
      alert('Error adjusting credits');
    } finally {
      setAdjusting(false);
    }
  };

  const getTotalCredits = (user) => {
    if (!user.creditBalances) return 0;
    return user.creditBalances.reduce((sum, b) => sum + (b.balance || 0), 0);
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const windowMinutes = Math.round(rateLimitSettings.windowMs / 60000);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <div className="flex gap-2 flex-wrap">
          <Link 
            to="/admin/orders" 
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Manage Orders
          </Link>
          <Link 
            to="/admin/analytics" 
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
          >
            Analytics
          </Link>
          <Link 
            to="/admin/education" 
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            📚 Education
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Users</div>
            <div className="text-2xl font-bold">{stats.totalUsers || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Orders</div>
            <div className="text-2xl font-bold">{stats.totalOrders || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Revenue</div>
            <div className="text-2xl font-bold text-green-600">${(stats.totalRevenue || 0).toFixed(2)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Products</div>
            <div className="text-2xl font-bold">{stats.totalProducts || 0}</div>
          </div>
        </div>
      )}

      {/* Rate Limit Control Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Rate Limit Control</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="mb-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={rateLimitSettings.enabled !== false}
                  onChange={(e) => setRateLimitSettings({ ...rateLimitSettings, enabled: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>Enable Rate Limiting</span>
              </label>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Max Attempts</label>
              <input
                type="number"
                min="1"
                max="100"
                value={rateLimitSettings.maxAttempts || 5}
                onChange={(e) => setRateLimitSettings({ ...rateLimitSettings, maxAttempts: parseInt(e.target.value) })}
                className="w-32 border rounded px-3 py-2"
                disabled={!rateLimitSettings.enabled}
              />
              <p className="text-xs text-gray-500 mt-1">Number of login attempts allowed</p>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Time Window (minutes)</label>
              <input
                type="number"
                min="1"
                max="60"
                value={windowMinutes}
                onChange={(e) => setRateLimitSettings({ ...rateLimitSettings, windowMs: parseInt(e.target.value) * 60000 })}
                className="w-32 border rounded px-3 py-2"
                disabled={!rateLimitSettings.enabled}
              />
              <p className="text-xs text-gray-500 mt-1">Time window for counting attempts</p>
            </div>
            <button
              onClick={updateRateLimit}
              disabled={updatingRateLimit}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {updatingRateLimit ? 'Updating...' : 'Apply Settings'}
            </button>
          </div>
          <div className="border-l pl-6">
            <h3 className="font-semibold mb-2">Demo Mode</h3>
            <p className="text-sm text-gray-600 mb-3">
              Temporarily disable rate limiting for presentations or testing.
            </p>
            <div className="flex gap-2">
              <input
                type="number"
                min="15"
                max="480"
                value={demoModeMinutes}
                onChange={(e) => setDemoModeMinutes(parseInt(e.target.value))}
                className="w-24 border rounded px-3 py-2"
              />
              <span className="self-center text-sm">minutes</span>
              <button
                onClick={enableDemoMode}
                disabled={updatingRateLimit}
                className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 disabled:bg-gray-400"
              >
                Enable Demo Mode
              </button>
            </div>
            {rateLimitSettings.demoModeUntil && new Date(rateLimitSettings.demoModeUntil) > new Date() && (
              <p className="text-xs text-green-600 mt-2">
                Demo mode active until {new Date(rateLimitSettings.demoModeUntil).toLocaleTimeString()}
              </p>
            )}
            {rateLimitSettings.enabled === false && !rateLimitSettings.demoModeUntil && (
              <p className="text-xs text-orange-600 mt-2">
                Rate limiting is disabled (Demo Mode active)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">User Credit Management</h2>
          
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full max-w-md border rounded px-3 py-2"
            />
          </div>
          
          {loading ? (
            <div className="text-center py-8">Loading users...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Credits</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.map(user => (
                    <tr key={user._id}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="capitalize">{user.role}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        ${getTotalCredits(user).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                        >
                          Adjust Credits
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'education' && (
        <div className="bg-white rounded-lg shadow p-6">
          <EducationModuleManager 
            role="admin"
            modules={educationModules}
            onRefresh={fetchEducationModules}
            loading={loadingEducation}
          />
        </div>
      )}

      {/* Credit Adjustment Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Adjust Credits for {selectedUser.name}</h2>
            <p className="text-gray-600 mb-4">Current total: ${getTotalCredits(selectedUser).toFixed(2)}</p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Amount (+ to add, - to remove)</label>
              <input
                type="number"
                step="0.01"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="e.g., 10.00 or -5.00"
                className="w-full border rounded px-3 py-2"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Reason</label>
              <input
                type="text"
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                placeholder="e.g., Customer compensation, Referral bonus"
                className="w-full border rounded px-3 py-2"
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAdjustCredits(selectedUser._id)}
                disabled={adjusting}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
              >
                {adjusting ? 'Processing...' : 'Apply Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
